import { useState, useEffect } from "react";
import axios from "axios";
import { FileText, Globe, Shield, Scale, TrendingUp, Search, ExternalLink, ChevronDown, ChevronUp } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, LineChart, Line, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis } from "recharts";
import { DashboardSkeleton } from "../common/DashboardSkeleton";
import { usePrefetchedData } from "../common/DataPrefetch";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const TYPE_COLORS = { legislation: "#ef4444", strategy: "#3b82f6", guideline: "#a855f7", standard: "#10b981", funding: "#f59e0b" };
const TYPE_LABELS = { legislation: "Legislation", strategy: "Strategy", guideline: "Guideline", standard: "Standard", funding: "Funding" };
const typeLabel = t => TYPE_LABELS[t] || (t || "").replace(/[-_]/g, " ").replace(/\b\w/g, c => c.toUpperCase());
const ENF_COLORS = { mandatory: "#ef4444", voluntary: "#f59e0b", advisory: "#3b82f6" };

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-[#0c1222] border border-white/10 rounded-lg px-3 py-2 text-xs shadow-xl">
      <p className="font-bold text-white mb-1">{label}</p>
      {payload.map((p) => (
        <p key={p.name || p.dataKey} style={{ color: p.color || "#94a3b8" }}>{p.name}: {typeof p.value === "number" ? p.value.toLocaleString() : p.value}</p>
      ))}
    </div>
  );
};

const Stat = ({ icon: Icon, label, value, color, sub }) => (
  <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-4">
    <div className="flex items-center gap-2 mb-1">
      <Icon className="w-3.5 h-3.5" style={{ color }} />
      <span className="text-[9px] font-bold uppercase tracking-wider" style={{ color: `${color}90` }}>{label}</span>
    </div>
    <p className="text-2xl font-black text-white">{value}</p>
    {sub && <p className="text-[10px] text-slate-500 mt-0.5">{sub}</p>}
  </div>
);

export const PolicyObservatory = () => {
  const prefetched = usePrefetchedData();
  const [stats, setStats] = useState(null);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterEnf, setFilterEnf] = useState("");
  const [filterRegion, setFilterRegion] = useState("");
  const [expandedPolicy, setExpandedPolicy] = useState(null);

  // Sync from prefetch store  
  useEffect(() => {
    if (prefetched.policyStats && !stats) setStats(prefetched.policyStats);
    if (prefetched.policyList && policies.length === 0) {
      setPolicies(prefetched.policyList.policies || []);
    }
    if ((prefetched.policyStats || stats) && (prefetched.policyList || policies.length > 0)) {
      setLoading(false);
    }
  });

  // Fallback fetch — only if prefetch hasn't delivered
  useEffect(() => {
    const timer = setTimeout(() => {
      if (stats && policies.length > 0) return; // prefetch already arrived
      Promise.allSettled([
        stats ? Promise.resolve({ data: stats }) : axios.get(`${API}/policy/stats`),
        policies.length > 0 ? Promise.resolve({ data: { policies } }) : axios.get(`${API}/policy/list?limit=200`),
      ]).then(([s, p]) => {
        if (s.status === "fulfilled") setStats(prev => prev || s.value.data);
        if (p.status === "fulfilled" && policies.length === 0) setPolicies(p.value.data.policies || []);
      }).finally(() => setLoading(false));
    }, 1500);
    return () => clearTimeout(timer);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const filteredPolicies = policies.filter(p => {
    if (filterType && p.policy_type !== filterType) return false;
    if (filterEnf && p.enforcement_level !== filterEnf) return false;
    if (filterRegion && p.region !== filterRegion) return false;
    if (searchQuery && !p.policy_name.toLowerCase().includes(searchQuery.toLowerCase()) && !p.country.toLowerCase().includes(searchQuery.toLowerCase())) return false;
    return true;
  });

  if (loading) {
    return <DashboardSkeleton galaxyColor="emerald" />;
  }

  if (!stats) return null;
  const ciiColor = stats.avg_cii >= 60 ? "#10b981" : stats.avg_cii >= 30 ? "#f59e0b" : "#ef4444";

  return (
    <div className="space-y-6 animate-fade-in pb-8" data-testid="policy-observatory">
      {/* Hero */}
      <div className="relative rounded-2xl overflow-hidden" style={{ background: "linear-gradient(135deg, #0a0f1a 0%, #0a1520 50%, #0f0a1a 100%)", border: "1px solid rgba(6,182,212,0.12)" }}>
        <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 30% 50%, rgba(6,182,212,0.06), transparent 60%)" }} />
        <div className="relative px-8 py-8">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
                <span className="text-[10px] font-bold text-cyan-400/70 uppercase tracking-[0.2em]">Galaxy 4</span>
              </div>
              <h1 className="text-2xl sm:text-3xl font-black text-white">Policy Observatory</h1>
              <p className="text-sm text-slate-400 mt-1">Tracking AI governance frameworks across {stats.countries_count} countries and international bodies</p>
            </div>
            <div className="flex items-center gap-4 flex-shrink-0">
              <div className="relative w-20 h-20 flex items-center justify-center">
                <svg width="80" height="80" viewBox="0 0 80 80">
                  <circle cx="40" cy="40" r="34" fill="none" stroke="rgba(255,255,255,0.06)" strokeWidth="5" />
                  <circle cx="40" cy="40" r="34" fill="none" stroke={ciiColor} strokeWidth="5"
                    strokeDasharray={`${stats.avg_cii * 2.14} ${214 - stats.avg_cii * 2.14}`}
                    strokeDashoffset="54" strokeLinecap="round" style={{ transition: "stroke-dasharray 1s ease" }} />
                </svg>
                <span className="absolute text-xl font-black" style={{ color: ciiColor }}>{stats.avg_cii}</span>
              </div>
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Policy CII</p>
                <p className="text-[9px] text-slate-500">Avg across {stats.total_policies} policies</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat icon={FileText} label="Total Policies" value={stats.total_policies} color="#06b6d4" />
        <Stat icon={Globe} label="Countries" value={stats.countries_count} color="#10b981" />
        <Stat icon={Shield} label="Mandatory" value={stats.enforcement?.find(e => e.level === "mandatory")?.count || 0} color="#ef4444" sub="With binding enforcement" />
        <Stat icon={Scale} label="Avg CII" value={stats.avg_cii} color={ciiColor} sub="Policy consciousness score" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Timeline */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Policy Adoption Timeline</h3>
          <div className="h-48">
            <ResponsiveContainer>
              <BarChart data={stats.timeline || []}>
                <XAxis dataKey="year" tick={{ fill: "#64748b", fontSize: 10 }} />
                <YAxis tick={{ fill: "#64748b", fontSize: 10 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Policies" fill="#06b6d4" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Policy Types */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Policy Types</h3>
          <div className="h-48 flex items-center justify-center">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={(stats.policy_types || []).map(t => ({ name: typeLabel(t.type), value: t.count, fill: TYPE_COLORS[t.type] || "#64748b" }))} cx="50%" cy="50%" outerRadius={70} innerRadius={40} paddingAngle={2} dataKey="value">
                  {(stats.policy_types || []).map((t) => (
                    <Cell key={t.type} fill={TYPE_COLORS[t.type] || "#64748b"} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2 mt-2 justify-center">
            {(stats.policy_types || []).map(t => (
              <span key={t.type} className="text-[9px] px-2 py-0.5 rounded-full" style={{ color: TYPE_COLORS[t.type], background: `${TYPE_COLORS[t.type]}15`, border: `1px solid ${TYPE_COLORS[t.type]}25` }}>
                {typeLabel(t.type)}: {t.count}
              </span>
            ))}
          </div>
        </div>

        {/* CII Dimension Coverage */}
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">CII Dimension Coverage</h3>
          <div className="h-56">
            <ResponsiveContainer>
              <RadarChart data={(stats.dimension_coverage || []).map(d => ({ subject: d.dimension?.length > 12 ? d.dimension.substring(0, 10) + ".." : d.dimension, coverage: d.percent }))}>
                <PolarGrid stroke="rgba(255,255,255,0.06)" />
                <PolarAngleAxis dataKey="subject" tick={{ fill: "#94a3b8", fontSize: 8 }} />
                <PolarRadiusAxis tick={{ fill: "#64748b", fontSize: 8 }} domain={[0, 100]} />
                <Radar name="Coverage %" dataKey="coverage" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.2} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Regional Breakdown */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Regional Breakdown</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(stats.regions || []).map(r => {
            const regionCiiColor = r.avg_cii >= 60 ? "#10b981" : r.avg_cii >= 30 ? "#f59e0b" : "#ef4444";
            return (
              <div key={r.region} className="bg-white/[0.02] border border-white/[0.04] rounded-lg p-4 hover:bg-white/[0.03] transition-colors cursor-pointer"
                onClick={() => setFilterRegion(filterRegion === r.region ? "" : r.region)}
                data-testid={`region-${r.region}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-bold text-white">{r.region}</span>
                  <span className="text-lg font-black" style={{ color: regionCiiColor }}>{r.avg_cii}</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-white/[0.04] rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${r.avg_cii}%`, background: regionCiiColor }} />
                  </div>
                  <span className="text-[10px] text-slate-400">{r.count} policies</span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Orientation Breakdown + Country Leaderboard */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5" data-testid="orientation-breakdown">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Policy Orientation</h3>
          <div className="space-y-3">
            {(stats.orientations || []).map(o => {
              const color = {care:"#10b981",utility:"#3b82f6",creative:"#a855f7",extraction:"#ef4444"}[o.orientation] || "#64748b";
              const pct = Math.round(o.count / stats.total_policies * 100);
              return (
                <div key={o.orientation} className="flex items-center gap-3">
                  <div className="w-16 text-right">
                    <span className="text-xs font-bold capitalize" style={{ color }}>{o.orientation}</span>
                  </div>
                  <div className="flex-1 h-5 bg-white/[0.04] rounded-full overflow-hidden relative">
                    <div className="h-full rounded-full flex items-center justify-end pr-2 transition-all duration-500" style={{ width: `${pct}%`, background: `${color}30`, borderRight: `2px solid ${color}` }}>
                      <span className="text-[9px] font-bold" style={{ color }}>{o.count}</span>
                    </div>
                  </div>
                  <span className="text-[10px] text-slate-400 w-16">{pct}% &middot; CII {o.avg_cii}</span>
                </div>
              );
            })}
          </div>
          <p className="text-[9px] text-slate-500 mt-3 pt-3 border-t border-white/[0.04]">
            Orientation reflects primary intent: <span className="text-emerald-400">Care</span> = citizen protection, <span className="text-blue-400">Utility</span> = economic growth, <span className="text-purple-400">Creative</span> = innovation enablement, <span className="text-red-400">Extraction</span> = surveillance/military
          </p>
        </div>

        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5" data-testid="country-leaderboard">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-4">Country CII Leaders</h3>
          <div className="space-y-2">
            {(stats.country_leaders || []).map((c, i) => {
              const color = c.avg_cii >= 60 ? "#10b981" : c.avg_cii >= 30 ? "#f59e0b" : "#ef4444";
              return (
                <div key={c.country} className="flex items-center gap-3 py-1">
                  <span className="text-[10px] text-slate-500 w-5 text-right">{i + 1}</span>
                  <span className="text-xs font-bold text-white flex-1">{c.country}</span>
                  <span className="text-[9px] text-slate-400">{c.count} policies</span>
                  <span className="text-sm font-black w-10 text-right" style={{ color }}>{c.avg_cii}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* Policy Explorer */}
      <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl overflow-hidden" data-testid="policy-explorer">
        <div className="px-5 py-4 border-b border-white/[0.06]">
          <h3 className="text-sm font-bold text-white mb-3">Policy Explorer</h3>
          <div className="flex flex-wrap gap-3">
            <div className="relative flex-1 min-w-48">
              <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
              <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Search policies or countries..."
                className="w-full pl-8 pr-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white placeholder:text-slate-500 focus:outline-none focus:border-cyan-500/30"
                data-testid="policy-search" />
            </div>
            <select value={filterType} onChange={e => setFilterType(e.target.value)} className="px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white" data-testid="filter-policy-type">
              <option value="">All Types</option>
              {Object.entries(TYPE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
            </select>
            <select value={filterEnf} onChange={e => setFilterEnf(e.target.value)} className="px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white" data-testid="filter-enforcement">
              <option value="">All Enforcement</option>
              <option value="mandatory">Mandatory</option>
              <option value="voluntary">Voluntary</option>
              <option value="advisory">Advisory</option>
            </select>
            <select value={filterRegion} onChange={e => setFilterRegion(e.target.value)} className="px-3 py-2 bg-white/[0.04] border border-white/[0.08] rounded-lg text-xs text-white" data-testid="filter-region">
              <option value="">All Regions</option>
              {(stats.regions || []).map(r => <option key={r.region} value={r.region}>{r.region}</option>)}
            </select>
          </div>
        </div>

        <div className="divide-y divide-white/[0.03]">
          {filteredPolicies.length === 0 && (
            <div className="text-center py-12">
              <FileText className="w-10 h-10 text-slate-700 mx-auto mb-2" />
              <p className="text-sm text-slate-400">No policies match your filters</p>
            </div>
          )}
          {filteredPolicies.map((p, i) => {
            const isExpanded = expandedPolicy === i;
            const cColor = p.policy_cii >= 60 ? "#10b981" : p.policy_cii >= 30 ? "#f59e0b" : "#ef4444";
            return (
              <div key={`${p.policy_name}-${p.country}`} className="hover:bg-white/[0.02] transition-colors">
                <div className="px-5 py-3 flex items-center gap-4 cursor-pointer" onClick={() => setExpandedPolicy(isExpanded ? null : i)} data-testid={`policy-row-${i}`}>
                  <div className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: {care:"#10b981",utility:"#3b82f6",creative:"#a855f7",extraction:"#ef4444"}[p.value_orientation] || "#64748b" }} title={p.value_orientation} />
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-bold text-white truncate">{p.policy_name}</p>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[10px] text-slate-400">{p.country}</span>
                      <span className="text-[10px] text-slate-600">&middot;</span>
                      <span className="text-[10px] text-slate-400">{p.year_adopted}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 flex-shrink-0">
                    <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ color: TYPE_COLORS[p.policy_type], background: `${TYPE_COLORS[p.policy_type]}15`, border: `1px solid ${TYPE_COLORS[p.policy_type]}25` }}>
                      {typeLabel(p.policy_type)}
                    </span>
                    <span className="text-[9px] px-2 py-0.5 rounded-full" style={{ color: ENF_COLORS[p.enforcement_level], background: `${ENF_COLORS[p.enforcement_level]}15`, border: `1px solid ${ENF_COLORS[p.enforcement_level]}25` }}>
                      {p.enforcement_level}
                    </span>
                    <span className="text-sm font-black w-10 text-right" style={{ color: cColor }}>{p.policy_cii}</span>
                    {isExpanded ? <ChevronUp className="w-4 h-4 text-slate-500" /> : <ChevronDown className="w-4 h-4 text-slate-500" />}
                  </div>
                </div>
                {isExpanded && (
                  <div className="px-5 pb-4 space-y-3 animate-fade-in">
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div className="text-[10px]"><span className="text-slate-500">Scope:</span> <span className="text-white">{p.scope}</span></div>
                      <div className="text-[10px]"><span className="text-slate-500">Region:</span> <span className="text-white">{p.region}</span></div>
                      <div className="text-[10px]"><span className="text-slate-500">Year:</span> <span className="text-white">{p.year_adopted}</span></div>
                      <div className="text-[10px]"><span className="text-slate-500">Source:</span> <span className="text-white">{p.source}</span></div>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 mb-1">Areas Covered:</p>
                      <div className="flex flex-wrap gap-1">
                        {(p.areas_covered || []).map(a => (
                          <span key={a} className="text-[9px] px-2 py-0.5 bg-cyan-500/10 border border-cyan-500/15 rounded-full text-cyan-300">{a}</span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <p className="text-[10px] text-slate-500 mb-1">CII Provisions:</p>
                      <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
                        {[
                          { key: "care_provisions", label: "Care" },
                          { key: "diversity_provisions", label: "Diversity" },
                          { key: "governance_provisions", label: "Governance" },
                          { key: "geographic_equity", label: "Geographic Eq." },
                          { key: "extraction_restrictions", label: "Extract. Restrict." },
                          { key: "inclusion_mandates", label: "Inclusion" },
                          { key: "agency_protections", label: "Agency" },
                        ].map(d => (
                          <div key={d.key} className={`text-[9px] px-2 py-1 rounded border ${p[d.key] ? "bg-emerald-500/10 border-emerald-500/20 text-emerald-300" : "bg-white/[0.02] border-white/[0.04] text-slate-500"}`}>
                            {p[d.key] ? "\u2713" : "\u2717"} {d.label}
                          </div>
                        ))}
                      </div>
                    </div>
                    {p.source_url && (
                      <a href={p.source_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[10px] text-cyan-400 hover:text-cyan-300" data-testid={`policy-source-${i}`}>
                        View source <ExternalLink className="w-3 h-3" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Top & Bottom CII */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5" data-testid="top-policies">
          <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-3">Highest CII Policies</h3>
          <div className="space-y-2">
            {(stats.top_policies || []).map((p) => (
              <div key={p.policy_name} className="flex items-center justify-between py-1.5">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-white truncate">{p.policy_name}</p>
                  <p className="text-[10px] text-slate-500">{p.country} &middot; {p.year_adopted}</p>
                </div>
                <span className="text-sm font-black text-emerald-400 ml-3">{p.policy_cii}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white/[0.02] border border-white/[0.06] rounded-xl p-5" data-testid="bottom-policies">
          <h3 className="text-xs font-bold text-red-400 uppercase tracking-wider mb-3">Lowest CII Policies</h3>
          <div className="space-y-2">
            {(stats.lowest_policies || []).map((p) => (
              <div key={p.policy_name} className="flex items-center justify-between py-1.5">
                <div className="flex-1 min-w-0">
                  <p className="text-xs font-bold text-white truncate">{p.policy_name}</p>
                  <p className="text-[10px] text-slate-500">{p.country} &middot; {p.year_adopted}</p>
                </div>
                <span className="text-sm font-black text-red-400 ml-3">{p.policy_cii}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
